import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, Cell, LabelList,
} from 'recharts';
import styles from './RelatoriosV2.module.css';

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtNum = (v) => Number(v || 0).toLocaleString('pt-BR');
const fmtBRLCompact = (v) => {
  const n = Number(v) || 0;
  if (n >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `R$ ${(n / 1_000).toFixed(0)}k`;
  return fmtBRL(n);
};
const fmtDateBR = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

// Mensagem em linguagem simples quando os dados estão atrasados (sem jargão técnico).
const mensagemDefasagem = (cacheStatus) => {
  if (!cacheStatus?.defasado) return null;
  if (!cacheStatus.ultima_venda_disponivel) {
    return 'Ainda não há vendas recentes disponíveis para esta loja.';
  }
  return `Dados até ${fmtDateBR(cacheStatus.ultima_venda_disponivel)} — atualização atrasada, os últimos dias ainda não aparecem aqui.`;
};

const LOJAS_VALIDAS = { ecommerce: 'E-commerce', centro: 'Loja Centro', caxias: 'Loja Caxias' };

const MODOS = [
  { key: 'mesmo_mes', label: 'Mesmo mês' },
  { key: 'janela', label: 'Janela 30d' },
  { key: 'ytd', label: 'YTD (ano corrente)' },
];

const LojaDetalhe = () => {
  const { loja } = useParams();
  const [dashboard, setDashboard] = useState(null);
  const [comparativo, setComparativo] = useState(null);
  const [modoComparativo, setModoComparativo] = useState('mesmo_mes');
  const [anosComparativo, setAnosComparativo] = useState(5);
  const [loading, setLoading] = useState(true);
  const [loadingComp, setLoadingComp] = useState(false);

  const lojaLabel = LOJAS_VALIDAS[loja] || 'Loja';
  const lojaValida = !!LOJAS_VALIDAS[loja];

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/relatorios-faturamento-v2/dashboard', { params: { loja } });
      setDashboard(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [loja]);

  const loadComparativo = useCallback(async () => {
    setLoadingComp(true);
    try {
      const { data } = await axios.get('/api/relatorios-faturamento-v2/comparativo', {
        params: { loja, modo: modoComparativo, anos: anosComparativo },
      });
      setComparativo(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingComp(false);
    }
  }, [loja, modoComparativo, anosComparativo]);

  useEffect(() => { if (lojaValida) loadDashboard(); }, [loadDashboard, lojaValida]);
  useEffect(() => { if (lojaValida) loadComparativo(); }, [loadComparativo, lojaValida]);

  if (!lojaValida) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loja inválida. <Link to="/relatorios-faturamento-v2">Voltar</Link></div>
      </div>
    );
  }

  if (loading) return <div className={styles.container}><div className={styles.loading}>Carregando...</div></div>;
  if (!dashboard?.success) return <div className={styles.container}><div className={styles.loading}>Erro ao carregar dashboard.</div></div>;

  const { kpis, comparativo: comp, top_produtos, faturamento_diario_30d } = dashboard;

  const varMesAnt = comp.variacao_mes_anterior || 0;
  const varAnoAnt = comp.variacao_ano_anterior || 0;
  const varClass = (v) => v > 0 ? styles.positiva : v < 0 ? styles.negativa : styles.neutra;
  const varArrow = (v) => v > 0 ? '▲' : v < 0 ? '▼' : '—';

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <Link to="/relatorios-faturamento-v2" className={styles.backLink}>← Voltar ao consolidado</Link>
          <h1 className={styles.title} style={{ marginTop: '0.4rem' }}>{lojaLabel}</h1>
          <div className={styles.subtitle}>
            Período: {dashboard.periodo.inicio} a {dashboard.periodo.fim}
          </div>
        </div>
      </div>

      {mensagemDefasagem(dashboard.cache_status) && (
        <div className="alert alert-warning py-2 px-3 mb-3" style={{ fontSize: '0.85rem' }}>
          ⚠️ {mensagemDefasagem(dashboard.cache_status)}
        </div>
      )}

      {/* KPIs principais */}
      <div className={styles.kpiGrid}>
        <div className={`${styles.kpiCardLarge} ${styles.success}`}>
          <div className={styles.kpiLabel}>Faturamento do Mês</div>
          <div className={`${styles.kpiValue} ${styles.big}`} style={{ fontSize: '1.7rem' }}>{fmtBRL(kpis.mes.faturamento)}</div>
          <div className={styles.kpiSub}>{fmtNum(kpis.mes.num_vendas)} vendas</div>
        </div>
        <div className={styles.kpiCardLarge}>
          <div className={styles.kpiLabel}>Ticket Médio</div>
          <div className={`${styles.kpiValue} ${styles.big}`} style={{ fontSize: '1.7rem' }}>{fmtBRL(kpis.mes.ticket_medio)}</div>
        </div>
        <div className={`${styles.kpiCardLarge} ${styles.info}`}>
          <div className={styles.kpiLabel}>Produtos Vendidos</div>
          <div className={`${styles.kpiValue} ${styles.big}`} style={{ fontSize: '1.7rem' }}>{fmtNum(kpis.mes.qtde_itens)}</div>
          <div className={styles.kpiSub}>unidades</div>
        </div>
        <div className={`${styles.kpiCardLarge} ${styles.warning}`}>
          <div className={styles.kpiLabel}>Descontos Concedidos</div>
          <div className={`${styles.kpiValue} ${styles.big}`} style={{ fontSize: '1.7rem' }}>{fmtBRL(kpis.mes.descontos)}</div>
          <div className={styles.kpiSub}>{kpis.mes.percentual_desconto.toFixed(1)}% do bruto</div>
        </div>
        {/* Trocas — só existe na loja física (o e-commerce não manda kpis.mes.trocas) */}
        {kpis.mes.trocas && (
          <div className={`${styles.kpiCardLarge} ${styles.info}`}>
            <div className={styles.kpiLabel}>Trocas</div>
            {kpis.mes.trocas.disponivel === false ? (
              <div className={styles.kpiValue} style={{ fontSize: '1.1rem', color: '#ADB5BD' }}>Sem dado no período</div>
            ) : (
              <>
                <div
                  className={`${styles.kpiValue} ${styles.big}`}
                  style={{ fontSize: '1.7rem' }}
                  title={`Valor de venda de referência dos itens trocados: ${fmtBRL(kpis.mes.trocas.valor_venda_referencia)}`}
                >
                  {fmtBRL(kpis.mes.trocas.valor_trocado)}
                </div>
                <div className={styles.kpiSub}>{fmtNum(kpis.mes.trocas.quantidade)} unidades trocadas</div>
              </>
            )}
            {kpis.mes.trocas.disponivel !== false && kpis.mes.trocas.cobertura_parcial && kpis.mes.trocas.cobertura_ate && (
              <div className={styles.kpiSub} style={{ color: '#E67E22' }}>dados até {fmtDateBR(kpis.mes.trocas.cobertura_ate)}</div>
            )}
          </div>
        )}
        <div className={styles.kpiCardLarge}>
          <div className={styles.kpiLabel}>Vendas Ontem</div>
          <div className={`${styles.kpiValue} ${styles.big}`} style={{ fontSize: '1.7rem' }}>{fmtBRL(kpis.ontem.faturamento)}</div>
          <div className={styles.kpiSub}>{fmtNum(kpis.ontem.num_vendas)} vendas · {fmtNum(kpis.ontem.qtde_itens)} itens</div>
        </div>
        <div className={`${styles.kpiCardLarge} ${styles.info}`}>
          <div className={styles.kpiLabel}>Vendas Hoje</div>
          <div className={`${styles.kpiValue} ${styles.big}`} style={{ fontSize: '1.7rem' }}>{fmtBRL(kpis.hoje.faturamento)}</div>
          <div className={styles.kpiSub}>parcial em tempo real · {fmtNum(kpis.hoje.num_vendas)} vendas</div>
        </div>
        <div className={`${styles.kpiCardLarge} ${varMesAnt >= 0 ? styles.success : styles.danger}`}>
          <div className={styles.kpiLabel}>vs Mês Anterior</div>
          <div className={`${styles.kpiValue} ${styles.big}`} style={{ fontSize: '1.7rem' }}>
            <span className={`${styles.variacao} ${varClass(varMesAnt)}`} style={{ fontSize: '1.2rem' }}>
              {varArrow(varMesAnt)} {Math.abs(varMesAnt).toFixed(1)}%
            </span>
          </div>
          <div className={styles.kpiSub}>{fmtBRL(comp.mes_anterior.faturamento)} no mês anterior</div>
        </div>
        <div className={`${styles.kpiCardLarge} ${varAnoAnt >= 0 ? styles.success : styles.danger}`}>
          <div className={styles.kpiLabel}>vs Mesmo mês ano anterior</div>
          <div className={`${styles.kpiValue} ${styles.big}`} style={{ fontSize: '1.7rem' }}>
            <span className={`${styles.variacao} ${varClass(varAnoAnt)}`} style={{ fontSize: '1.2rem' }}>
              {varArrow(varAnoAnt)} {Math.abs(varAnoAnt).toFixed(1)}%
            </span>
          </div>
          <div className={styles.kpiSub}>{fmtBRL(comp.ano_anterior_mesmo_periodo.faturamento)} no ano anterior</div>
        </div>
      </div>

      {/* Faturamento diário 30d */}
      <div className={styles.chartCard} style={{ marginBottom: '1.5rem' }}>
        <h3>Faturamento últimos 30 dias</h3>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={faturamento_diario_30d}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" />
            <XAxis dataKey="dia" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={fmtBRLCompact} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => fmtBRL(v)} labelFormatter={(d) => `Dia: ${d}`} />
            <Line type="monotone" dataKey="valor" stroke="#6C5CE7" strokeWidth={2} name="Faturamento" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Comparativo histórico */}
      <div className={styles.chartCard} style={{ marginBottom: '1.5rem' }}>
        <h3>
          Comparativo Histórico
          <div className={styles.modoSelector}>
            {MODOS.map(m => (
              <button
                key={m.key}
                className={`${styles.modoBtn} ${modoComparativo === m.key ? styles.active : ''}`}
                onClick={() => setModoComparativo(m.key)}
              >
                {m.label}
              </button>
            ))}
            <select
              value={anosComparativo}
              onChange={(e) => setAnosComparativo(parseInt(e.target.value))}
              className={styles.modoBtn}
              style={{ background: '#fff', color: '#495057' }}
            >
              {[3, 4, 5, 6, 8, 10].map(n => <option key={n} value={n}>{n} anos</option>)}
            </select>
          </div>
        </h3>
        {loadingComp ? (
          <div className={styles.loading}>Carregando comparativo...</div>
        ) : !comparativo?.success ? (
          <div className={styles.empty}>Sem dados de comparativo</div>
        ) : (
          <>
            {comparativo.cobertura_cache?.min && (
              <div className={styles.coberturaCaption}>
                Histórico de vendas disponível de <strong>{fmtDateBR(comparativo.cobertura_cache.min)}</strong> a{' '}
                <strong>{fmtDateBR(comparativo.cobertura_cache.max)}</strong> — períodos fora dessa janela aparecem como "sem dados", não como venda zero.
              </div>
            )}
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={comparativo.dados}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={fmtBRLCompact} tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(v, name, props) => {
                    if (props?.payload?.sem_cobertura) return ['Sem dados no histórico', name];
                    if (props?.payload?.cobertura_parcial && name === 'Faturamento') return [`${fmtBRL(v)} (período incompleto)`, name];
                    return [name === 'Faturamento' ? fmtBRL(v) : fmtNum(v), name];
                  }}
                  labelFormatter={(l) => `Período: ${l}`}
                />
                <Legend />
                <Bar dataKey="faturamento" name="Faturamento" radius={[6, 6, 0, 0]}>
                  {comparativo.dados.map((entry, i) => {
                    const eAtual = i === comparativo.dados.length - 1;
                    const fill = entry.sem_cobertura
                      ? '#F1F3F5'
                      : eAtual
                        ? '#6C5CE7'
                        : entry.cobertura_parcial
                          ? '#D6CFFF'
                          : '#A29BFE';
                    return (
                      <Cell
                        key={i}
                        fill={fill}
                        stroke={entry.sem_cobertura ? '#CED4DA' : undefined}
                        strokeDasharray={entry.sem_cobertura ? '4 3' : undefined}
                      />
                    );
                  })}
                  <LabelList
                    dataKey="faturamento"
                    content={(props) => {
                      const { x, y, width, index } = props;
                      const entry = comparativo.dados[index];
                      if (!entry) return null;
                      if (entry.sem_cobertura) {
                        return (
                          <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={10} fill="#ADB5BD">
                            sem dados
                          </text>
                        );
                      }
                      if (entry.cobertura_parcial) {
                        return (
                          <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={10} fill="#E67E22">
                            parcial
                          </text>
                        );
                      }
                      return null;
                    }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div style={{ overflowX: 'auto', marginTop: '1rem' }}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Período</th>
                    <th style={{ textAlign: 'right' }}>Faturamento</th>
                    <th style={{ textAlign: 'right' }}>Itens</th>
                    <th style={{ textAlign: 'right' }}>Vendas</th>
                    <th style={{ textAlign: 'right' }}>Ticket Médio</th>
                    <th style={{ textAlign: 'right' }}>Descontos</th>
                    <th style={{ textAlign: 'right' }}>vs Atual</th>
                  </tr>
                </thead>
                <tbody>
                  {comparativo.dados.map((d, i, arr) => {
                    const atual = arr[arr.length - 1];
                    const eAtual = i === arr.length - 1;
                    const diff = (!d.sem_cobertura && !atual.sem_cobertura && atual.faturamento > 0)
                      ? ((d.faturamento - atual.faturamento) / atual.faturamento) * 100
                      : null;
                    return (
                      <tr key={d.label} style={eAtual ? { background: '#F4F1FF', fontWeight: 600 } : undefined}>
                        <td>
                          {d.label}{eAtual && ' (atual)'}
                          {d.cobertura_parcial && <span className={styles.parcialBadge}>parcial</span>}
                        </td>
                        {d.sem_cobertura ? (
                          <td colSpan={5} className={styles.semDadosCell}>Sem dados disponíveis nesse período</td>
                        ) : (
                          <>
                            <td style={{ textAlign: 'right' }}>{fmtBRL(d.faturamento)}</td>
                            <td style={{ textAlign: 'right' }}>{fmtNum(d.qtde_itens)}</td>
                            <td style={{ textAlign: 'right' }}>{fmtNum(d.num_vendas)}</td>
                            <td style={{ textAlign: 'right' }}>{fmtBRL(d.ticket_medio)}</td>
                            <td style={{ textAlign: 'right' }}>{fmtBRL(d.descontos)}</td>
                          </>
                        )}
                        <td style={{ textAlign: 'right' }}>
                          {diff === null ? '—' : (
                            <span className={`${styles.variacao} ${varClass(diff)}`}>
                              {varArrow(diff)} {Math.abs(diff).toFixed(1)}%
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Top 10 produtos */}
      <div className={styles.tableCard}>
        <h3>Top 10 Produtos do Mês</h3>
        <div style={{ overflowX: 'auto' }}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>#</th>
              <th>Código</th>
              <th>Descrição</th>
              <th style={{ textAlign: 'right' }}>Qtd vendida</th>
              <th style={{ textAlign: 'right' }}>Pedidos</th>
              <th style={{ textAlign: 'right' }}>Receita</th>
            </tr>
          </thead>
          <tbody>
            {top_produtos.length === 0 ? (
              <tr><td colSpan={6} className={styles.empty}>Sem dados</td></tr>
            ) : top_produtos.map((p, i) => {
              const rankCls = i === 0 ? styles.gold : i === 1 ? styles.silver : i === 2 ? styles.bronze : '';
              return (
                <tr key={p.codigo}>
                  <td><span className={`${styles.rankBadge} ${rankCls}`}>{i + 1}</span></td>
                  <td><strong>{p.codigo}</strong></td>
                  <td>{p.descricao}</td>
                  <td style={{ textAlign: 'right' }}>{fmtNum(p.qtde)}</td>
                  <td style={{ textAlign: 'right' }}>{fmtNum(p.pedidos)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtBRL(p.receita)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
};

export default LojaDetalhe;
